export const amazonSpOAuthLoginPath = "/amazon/oauth/login";
export const amazonSpOAuthCallbackPath = "/amazon/oauth/callback";
export const amazonSpOAuthLoginContextStorageKey =
  "merry.amazon_sp.oauth_login_context";

const amazonSpOAuthLoginContextTTL = 10 * 60 * 1_000;

export interface AmazonSpOAuthLoginContext {
  createdAt: number;
  redirectURI: string;
  state: string;
  version: string;
}

export function createAmazonSpOAuthLoginContext(
  authorizationURL: string,
  origin: string,
  now = Date.now(),
): AmazonSpOAuthLoginContext {
  const authorization = parseURL(
    authorizationURL,
    "amazon_sp_oauth_authorization_url_invalid",
  );
  if (
    authorization.protocol !== "https:" ||
    !isTrustedAmazonHost(authorization.hostname)
  ) {
    throw new Error("amazon_sp_oauth_authorization_url_untrusted");
  }
  const state = requiredQueryValue(
    authorization.searchParams,
    "state",
    "amazon_sp_oauth_state_missing",
  );
  const version = authorization.searchParams.get("version")?.trim() ?? "";
  if (version && version !== "beta") {
    throw new Error("amazon_sp_oauth_version_invalid");
  }
  const redirectURI = new URL(amazonSpOAuthCallbackPath, origin);
  if (redirectURI.protocol !== "https:") {
    throw new Error("amazon_sp_oauth_redirect_uri_insecure");
  }
  return { createdAt: now, redirectURI: redirectURI.toString(), state, version };
}

export function parseAmazonSpOAuthLoginContext(
  raw: string | null,
): AmazonSpOAuthLoginContext {
  if (!raw) throw new Error("amazon_sp_oauth_login_context_missing");
  let decoded: unknown;
  try {
    decoded = JSON.parse(raw);
  } catch {
    throw new Error("amazon_sp_oauth_login_context_invalid");
  }
  if (!isRecord(decoded)) {
    throw new Error("amazon_sp_oauth_login_context_invalid");
  }
  const state = stringField(decoded, "state");
  const redirectURI = stringField(decoded, "redirectURI");
  const version = stringField(decoded, "version");
  const createdAt = decoded.createdAt;
  if (
    !state ||
    !redirectURI ||
    (version && version !== "beta") ||
    typeof createdAt !== "number" ||
    !Number.isFinite(createdAt)
  ) {
    throw new Error("amazon_sp_oauth_login_context_invalid");
  }
  return { createdAt, redirectURI, state, version };
}

export function buildAmazonSpOAuthAmazonCallbackURL(
  loginSearch: string,
  context: AmazonSpOAuthLoginContext,
  origin: string,
  now = Date.now(),
) {
  if (
    context.createdAt > now + 60_000 ||
    now - context.createdAt > amazonSpOAuthLoginContextTTL
  ) {
    throw new Error("amazon_sp_oauth_login_context_expired");
  }
  const registeredRedirect = parseURL(
    context.redirectURI,
    "amazon_sp_oauth_redirect_uri_invalid",
  );
  const expectedRedirect = new URL(amazonSpOAuthCallbackPath, origin);
  if (
    registeredRedirect.toString() !== expectedRedirect.toString() ||
    registeredRedirect.protocol !== "https:"
  ) {
    throw new Error("amazon_sp_oauth_redirect_uri_mismatch");
  }

  const loginParams = new URLSearchParams(loginSearch);
  const callbackValue = requiredQueryValue(
    loginParams,
    "amazon_callback_uri",
    "amazon_sp_oauth_amazon_callback_missing",
  );
  const amazonState = requiredQueryValue(
    loginParams,
    "amazon_state",
    "amazon_sp_oauth_amazon_state_missing",
  );
  requiredQueryValue(
    loginParams,
    "selling_partner_id",
    "amazon_sp_oauth_seller_missing",
  );

  const incomingVersion = loginParams.get("version")?.trim() ?? "";
  if (incomingVersion && incomingVersion !== "beta") {
    throw new Error("amazon_sp_oauth_version_invalid");
  }
  if (
    incomingVersion &&
    context.version &&
    incomingVersion !== context.version
  ) {
    throw new Error("amazon_sp_oauth_version_mismatch");
  }
  const callback = parseURL(
    callbackValue,
    "amazon_sp_oauth_amazon_callback_invalid",
  );
  if (
    callback.protocol !== "https:" ||
    !isTrustedAmazonHost(callback.hostname) ||
    !callback.pathname.startsWith("/apps/authorize/confirm")
  ) {
    throw new Error("amazon_sp_oauth_amazon_callback_untrusted");
  }
  callback.searchParams.set("amazon_state", amazonState);
  callback.searchParams.set("state", context.state);
  callback.searchParams.set("redirect_uri", context.redirectURI);
  const version = incomingVersion || context.version;
  if (version) callback.searchParams.set("version", version);
  else callback.searchParams.delete("version");
  return callback.toString();
}

function parseURL(value: string, errorCode: string) {
  try {
    return new URL(value);
  } catch {
    throw new Error(errorCode);
  }
}

function requiredQueryValue(
  params: URLSearchParams,
  key: string,
  errorCode: string,
) {
  const value = params.get(key)?.trim() ?? "";
  if (!value) throw new Error(errorCode);
  return value;
}

function isTrustedAmazonHost(hostname: string) {
  const normalized = hostname.toLowerCase();
  return normalized === "amazon.com" || normalized.endsWith(".amazon.com");
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function stringField(value: Record<string, unknown>, key: string) {
  const field = value[key];
  return typeof field === "string" ? field.trim() : "";
}
